// src/screens/Today.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useSettings } from "../settings";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { useWalletId } from "../hooks/useWalletId";

import PullToRefreshHeader from "../components/PullToRefreshHeader";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

import PortfolioPLMiniChart from "./PortfolioPLMiniChart";
import PortfolioPositionsChart from "./PortfolioPositionsChart";

/* =========================
   small utils
========================= */

function fmtMoney(x, currency = "BRL") {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("pt-BR", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function fmtPct01(x, decimals = 1) {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtPct01Signed(x, decimals = 2) {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  const s = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${s}${Math.abs(n * 100).toFixed(decimals)}%`;
}

function fmtMoneySigned(x, currency = "BRL") {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  const abs = Math.abs(n);
  const formatted = fmtMoney(abs, currency);
  if (formatted === "—") return "—";
  return `${sign}${formatted}`;
}

function fmtMarketPrice(x, currency = "BRL") {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("pt-BR", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function toNumSmart(v) {
  if (v == null) return NaN;
  const s0 = String(v).trim();
  if (!s0) return NaN;
  if (s0.includes(",")) return Number(s0.replace(/\./g, "").replace(",", "."));
  return Number(s0);
}

function normSymbol(sym) {
  if (!sym) return "";
  let s = String(sym).trim().toUpperCase().replace(/\s+/g, "");
  if (s.endsWith(".SA")) s = s.slice(0, -3);
  if (s.includes(".")) s = s.split(".")[0];
  return s;
}

function isoDay(d) {
  if (!d) return null;
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
}

function addDaysISO(iso, days) {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function endOfTodayISO() {
  const now = new Date();
  const local = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return local.toISOString().slice(0, 10);
}

function monthKey(iso) {
  return String(iso || "").slice(0, 7);
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function computeMaxDrawdownPct01(series, field) {
  let peak = null;
  let maxDD = 0;
  for (const r of series || []) {
    const v = safeNum(r?.[field]);
    if (v == null) continue;
    if (peak == null || v > peak) peak = v;
    if (peak > 0) {
      const dd = (v - peak) / peak; // <= 0
      if (dd < maxDD) maxDD = dd;
    }
  }
  return maxDD;
}

function aggregateMonthlyLast(series) {
  const byM = new Map();
  for (const r of series || []) {
    const k = monthKey(r?.date);
    if (!k) continue;

    const prev = byM.get(k);
    const buy = Number(r?.buy_value_day || 0);
    const sell = Number(r?.sell_value_day || 0);

    if (!prev) {
      byM.set(k, {
        ...r,
        buy_value_day: buy,
        sell_value_day: sell,
      });
      continue;
    }

    prev.buy_value_day = Number(prev.buy_value_day || 0) + buy;
    prev.sell_value_day = Number(prev.sell_value_day || 0) + sell;

    if (String(r.date) >= String(prev.date)) {
      byM.set(k, { ...prev, ...r });
    } else {
      byM.set(k, prev);
    }
  }

  return Array.from(byM.values()).sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );
}

/* =========================
   ✅ SIM helpers (ideal)
========================= */

function findNavAtOrBefore(series, targetDateISO) {
  if (!Array.isArray(series) || !series.length || !targetDateISO) return null;
  for (let i = series.length - 1; i >= 0; i--) {
    const d = String(series[i]?.date || "");
    if (d && d <= targetDateISO) {
      const n = Number(series[i]?.nav);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
  }
  return null;
}

function buildIdealPct01ByDate(simSeries, evoFromISO, openedDateISO = null) {
  const out = new Map();
  const series = Array.isArray(simSeries) ? simSeries : [];
  if (!series.length) return out;

  const evoFrom = String(evoFromISO || "").slice(0, 10) || null;
  const opened =
    openedDateISO != null ? String(openedDateISO).slice(0, 10) : null;

  const refISO = evoFrom && opened && opened > evoFrom ? opened : evoFrom;
  if (!refISO) return out;

  const navRef = findNavAtOrBefore(series, refISO);
  if (!(navRef > 0)) return out;

  for (const r of series) {
    const d = String(r?.date || "").slice(0, 10);
    const nav = Number(r?.nav);
    if (!d) continue;
    if (!Number.isFinite(nav) || !(nav > 0)) continue;
    out.set(d, nav / navRef - 1);
  }
  return out;
}

/* =========================
   component
========================= */

export default function Today() {
  // ✅ IMPORTANTE: pega auth ANTES de usar
  const { auth, isAuthed } = useAuth();
  const customerId = auth?.customerId ?? null;

  // ✅ “pronto pra bater API protegida”
  const authReady = !!isAuthed && !!customerId;

  // ✅ escopo consistente pro React Query cache
  const todayScope = customerId || "guest";

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { settings, loading: settingsLoading } = useSettings();

  const ready = !!settings && !settingsLoading;
  const s = settings || {};

  const ROUTE_MONITORING = "/monitoringv2";
  const walletId = useWalletId();

  console.log("Today: render", { walletId, customerId });

  const [isNarrow, setIsNarrow] = useState(false);

  const [cashOpen, setCashOpen] = useState(false);
  const [cashValue, setCashValue] = useState("");
  const [cashSubmitting, setCashSubmitting] = useState(false);
  const [cashErr, setCashErr] = useState(null);

  // =========================
  // Evolução (filtros UI)
  // =========================
  const PERIODS = useMemo(
    () => [
      { key: "1m", label: "1m", days: 30 },
      { key: "3m", label: "3m", days: 90 },
      { key: "6m", label: "6m", days: 180 },
      { key: "1a", label: "1a", days: 365 },
      { key: "2a", label: "2a", days: 365 * 2 },
    ],
    []
  );

  const [evoPeriodKey, setEvoPeriodKey] = useState("1m");
  const [evoGran, setEvoGran] = useState("d1");
  const [evoSyncedRange, setEvoSyncedRange] = useState(null);

  // ===========================================================
  // ✅ Pull-to-refresh padrão (perene)
  // ===========================================================
  const refreshAll = useCallback(async () => {
    if (cashOpen) return;
    if (!ready) return;
    if (!authReady) return; // ✅ não tenta refetch sem auth

    // ✅ invalida pelo mesmo prefixo real usado nas queries
    await queryClient.invalidateQueries({ queryKey: ["today", todayScope] });

    await queryClient.refetchQueries({
      queryKey: ["today", todayScope],
      type: "active",
    });
  }, [queryClient, cashOpen, ready, authReady, todayScope]);

  const ptr = usePullToRefresh({
    enabled: true,
    isBlocked: cashOpen,
    startZoneY: 260,
    maxPull: 110,
    readyPull: 70,
    refreshHeight: 56,
    onRefresh: refreshAll,
  });

  useEffect(() => {
    const m = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsNarrow(Boolean(m.matches));
    onChange();
    try {
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    } catch {
      m.addListener(onChange);
      return () => m.removeListener(onChange);
    }
  }, []);

  function openAsset(symbol) {
    const sym = normSymbol(symbol);
    if (!sym) return;
    navigate(`/assets?symbol=${encodeURIComponent(sym)}`);
  }

  function openCashModal() {
    if (cashSubmitting) return;
    setCashErr(null);
    setCashValue("");
    setCashOpen(true);
  }

  function closeCashModal() {
    if (cashSubmitting) return;
    setCashOpen(false);
    setCashErr(null);
  }

  /* =========================
     ✅ React Query: SUMMARY only
  ========================= */

  const summaryQuery = useQuery({
    queryKey: ["today", todayScope, "walletSummary", walletId],
    enabled: ready && authReady && walletId != null,
    queryFn: async () => {
      const data = await apiFetch(
        `/api/wallet/summary?walletId=${encodeURIComponent(walletId)}`
      );
      return data || null;
    },
    staleTime: 15_000,
    gcTime: 10 * 60_000,
    refetchInterval: false,
  });

  const summaryLoading = summaryQuery.isLoading;
  const summaryErr = summaryQuery.error?.message || null;

  /* =========================
     ✅ Fatal somente se não tiver dado algum e houver erro
  ========================= */

  const anyErrObj = summaryQuery.error || null;
  const hasAnyData = !!summaryQuery.data;
  const showFatal = ready && authReady && !hasAnyData && !!anyErrObj;

  const safeSummary = showFatal ? null : summaryQuery.data;

  /* =========================
     ✅ Quotes map (para nome + variação)
  ========================= */

  const summaryQuotesMap = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(safeSummary?.quotes) ? safeSummary.quotes : [];

    for (const q of arr) {
      const sym = normSymbol(q?.symbol || "");
      const px = Number(q?.lastPrice);

      const ch = Number(q?.change);
      const chPct01 = Number(q?.changePct01);

      const nameRaw =
        q?.name ??
        q?.shortName ??
        q?.longName ??
        q?.displayName ??
        q?.shortname ??
        q?.longname ??
        null;

      const name = typeof nameRaw === "string" ? nameRaw.trim() || null : null;

      if (sym) {
        m.set(sym, {
          lastPrice: Number.isFinite(px) ? px : null,
          change: Number.isFinite(ch) ? ch : null,
          changePct01: Number.isFinite(chPct01) ? chPct01 : null,
          name,
          asOf: q?.asOf ?? null,
          error: q?.error ?? null,
        });
      }
    }
    return m;
  }, [safeSummary]);

  const quotesErr = summaryQuery.error?.message || null;
  const quotesUpdatedAt = useMemo(() => {
    const iso = safeSummary?.asOf || null;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [safeSummary]);

  function assetMetaOf(symbol) {
    const sym = normSymbol(symbol);
    if (!sym) return { name: null, sector: null };

    const q = summaryQuotesMap?.get?.(sym);
    const qName = typeof q?.name === "string" ? q.name.trim() : "";
    return { name: qName || null, sector: null };
  }

  /* =========================
     ✅ Wallet info (from summary)
  ========================= */

  const baseCurrency = useMemo(
    () => safeSummary?.base_currency || "BRL",
    [safeSummary]
  );

  const openedDateISO = useMemo(() => {
    const d = safeSummary?.opened_date || safeSummary?.openedDate || null;
    if (!d) return null;
    return String(d).slice(0, 10);
  }, [safeSummary]);

  const cashAvailable = useMemo(
    () => Number(safeSummary?.cash_available ?? 0),
    [safeSummary]
  );
  const cashReserved = useMemo(
    () => Number(safeSummary?.cash_reserved ?? 0),
    [safeSummary]
  );
  const cashUsable = useMemo(
    () => cashAvailable - cashReserved,
    [cashAvailable, cashReserved]
  );

  /* =========================
     ✅ Holdings (from summary.pl.per_asset)
  ========================= */

  const holdings = useMemo(() => {
    const per = Array.isArray(safeSummary?.pl?.per_asset)
      ? safeSummary.pl.per_asset
      : [];
    const out = [];
    for (const x of per) {
      const sym = normSymbol(x?.symbol);
      const qty = Number(x?.qty_open ?? 0);
      if (!sym || !(qty > 0)) continue;

      const lastFromSummary = Number(x?.last_price);
      const live = summaryQuotesMap?.get?.(sym);
      const lastFromQuotes = Number(live?.lastPrice);

      const lastPrice = Number.isFinite(lastFromSummary)
        ? lastFromSummary
        : Number.isFinite(lastFromQuotes)
        ? lastFromQuotes
        : null;

      const mvFromSummary = Number(x?.market_value);
      const marketValue =
        Number.isFinite(mvFromSummary) && mvFromSummary > 0
          ? mvFromSummary
          : lastPrice != null
          ? qty * lastPrice
          : null;

      const change = live?.change ?? null;
      const changePct01 = live?.changePct01 ?? null;

      out.push({
        symbol: sym,
        qty,
        lastPrice,
        marketValue,
        avgPrice: Number.isFinite(Number(x?.avg_open))
          ? Number(x.avg_open)
          : null,
        costOpen: Number.isFinite(Number(x?.cost_open))
          ? Number(x.cost_open)
          : null,
        change,
        changePct01,
        warnings: Array.isArray(x?.warnings) ? x.warnings : [],
      });
    }

    out.sort(
      (a, b) => (Number(b.marketValue) || 0) - (Number(a.marketValue) || 0)
    );
    return out;
  }, [safeSummary, summaryQuotesMap]);

  const positionsValueLive = useMemo(
    () => holdings.reduce((acc, h) => acc + (Number(h.marketValue) || 0), 0),
    [holdings]
  );

  const totalEquityLive = useMemo(
    () => cashUsable + positionsValueLive,
    [cashUsable, positionsValueLive]
  );

  const holdingsWithWeight = useMemo(() => {
    const denom = totalEquityLive > 0 ? totalEquityLive : 0;
    return holdings.map((h) => ({
      ...h,
      weight: denom > 0 && h.marketValue != null ? h.marketValue / denom : null,
    }));
  }, [holdings, totalEquityLive]);

  const isNewEmptyWallet = useMemo(() => {
    const eq = Number(totalEquityLive ?? 0);
    return holdings.length === 0 && cashUsable <= 0 && eq <= 0;
  }, [holdings.length, cashUsable, totalEquityLive]);

  const pl = safeSummary?.pl || null;

  // =========================
  // Evolução: datas do período
  // =========================
  const evoTo = useMemo(() => endOfTodayISO(), []);
  const evoFrom = useMemo(() => {
    const def = PERIODS.find((p) => p.key === evoPeriodKey) || PERIODS[1];
    return addDaysISO(evoTo, -def.days);
  }, [PERIODS, evoPeriodKey, evoTo]);

  useEffect(() => {
    setEvoSyncedRange(null);
  }, [evoPeriodKey, evoGran]);

  /* =========================
     ✅ React Query: Investment timeseries
  ========================= */

  const evoQuery = useQuery({
    queryKey: ["today", todayScope, "walletEvo", walletId, evoFrom, evoTo],
    enabled: ready && authReady && walletId != null,
    queryFn: async () => {
      const qs = new URLSearchParams({
        walletId: String(walletId),
        from: evoFrom,
        to: evoTo,
      });
      const data = await apiFetch(
        `/api/wallet/investment/timeseries?${qs.toString()}`
      );
      return data || null;
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchInterval: false,
  });

  const evoErr = evoQuery.error?.message || null;
  const evoLoading = evoQuery.isLoading;

  /* =========================
     ✅ React Query: Simulação (método ideal)
  ========================= */
  const simQuery = useQuery({
    queryKey: ["today", todayScope, "simSummary"],
    enabled: ready && authReady,
    queryFn: async () => {
      const data = await apiFetch(`/api/sim/summary`);
      return data || null;
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchInterval: false,
  });

  const simErr = simQuery.error?.message || null;

  const simSeries = useMemo(() => {
    const arr = Array.isArray(simQuery.data?.series) ? simQuery.data.series : [];
    return arr
      .map((r) => ({
        date: String(r?.date || "").slice(0, 10),
        nav: safeNum(r?.nav),
      }))
      .filter((r) => r.date && r.nav != null)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [simQuery.data]);

  const idealPct01ByDate = useMemo(() => {
    return buildIdealPct01ByDate(simSeries, evoFrom, openedDateISO);
  }, [simSeries, evoFrom, openedDateISO]);

  const evoSeriesRaw = useMemo(() => {
    const arr = Array.isArray(evoQuery.data?.series) ? evoQuery.data.series : [];
    return arr
      .map((r) => {
        const date = isoDay(r?.date) || String(r?.date || "").slice(0, 10);
        if (!date) return null;

        return {
          date,
          positions_value: safeNum(r?.positions_value),
          buy_value_day: safeNum(r?.buy_value_day) ?? 0,
          sell_value_day: safeNum(r?.sell_value_day) ?? 0,
          pct01: safeNum(r?.pct01),
          ideal_pct01: safeNum(idealPct01ByDate.get(date)),
          denom_total: safeNum(r?.denom_total),

          total_pl: safeNum(r?.total_pl),
          realized_pl_cum: safeNum(r?.realized_pl_cum),
          unrealized_pl: safeNum(r?.unrealized_pl),
          cost_open: safeNum(r?.cost_open),
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [evoQuery.data, idealPct01ByDate]);

  const evoSeries = useMemo(() => {
    if (evoGran === "m1") return aggregateMonthlyLast(evoSeriesRaw);
    return evoSeriesRaw;
  }, [evoGran, evoSeriesRaw]);

  // =========================
  // HOJE: último ponto (obrigatório)
  // =========================
  const todayPoint = useMemo(() => {
    if (!walletId || !safeSummary) return null;

    const pct01 = safeNum(pl?.total?.pct);
    const denom_total = safeNum(pl?.total?.denom_total);
    const total_pl = safeNum(pl?.total?.pl);

    return {
      date: evoTo,
      positions_value: safeNum(positionsValueLive),
      buy_value_day: 0,
      sell_value_day: 0,
      pct01,
      ideal_pct01: safeNum(idealPct01ByDate.get(evoTo)),
      denom_total,
      total_pl,

      realized_pl_cum: safeNum(pl?.realized?.pl),
      unrealized_pl: safeNum(pl?.unrealized?.pl),
      cost_open: safeNum(pl?.unrealized?.cost_open_total),

      _is_today: true,
    };
  }, [walletId, safeSummary, pl, evoTo, positionsValueLive, idealPct01ByDate]);

  // =========================
  // KPIs evolução
  // =========================
  const evoKpis = useMemo(() => {
    if (!evoSeries?.length) {
      return {
        ddPct01: null,
        buySum: null,
        sellSum: null,
      };
    }

    const ddPct01 = computeMaxDrawdownPct01(evoSeries, "positions_value");

    let buySum = 0;
    let sellSum = 0;
    for (const r of evoSeries) {
      buySum += Number(r?.buy_value_day || 0);
      sellSum += Number(r?.sell_value_day || 0);
    }

    return { ddPct01, buySum, sellSum };
  }, [evoSeries]);

  const idealReturnSelectedPct01 = useMemo(() => {
    const ret = simQuery.data?.returns || null;
    if (!ret) return null;

    if (evoPeriodKey === "1m") return safeNum(ret.d30);
    if (evoPeriodKey === "3m") return safeNum(ret.m3);
    if (evoPeriodKey === "6m") return safeNum(ret.m6);
    if (evoPeriodKey === "1a") return safeNum(ret.y1);
    if (evoPeriodKey === "2a") return safeNum(ret.y2);
    return null;
  }, [simQuery.data, evoPeriodKey]);

  async function submitCashAdjust() {
    if (!walletId) {
      setCashErr("walletId não encontrado (config).");
      return;
    }

    const delta = toNumSmart(cashValue);
    if (!Number.isFinite(delta) || delta <= 0) {
      setCashErr("Informe um valor > 0.");
      return;
    }

    setCashSubmitting(true);
    setCashErr(null);

    try {
      await apiFetch(`/api/wallet/cash/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          walletId: Number(walletId),
          delta: Number(delta.toFixed(2)),
        }),
      });

      await queryClient.invalidateQueries({
        queryKey: ["today", todayScope, "walletSummary", walletId],
      });

      await summaryQuery.refetch();

      setCashOpen(false);
      setCashValue("");
    } catch (e) {
      console.error(e);
      setCashErr(e?.message || "Falha ao ajustar caixa.");
    } finally {
      setCashSubmitting(false);
    }
  }

  // =========================
  // ✅ KPIs "Sua carteira"
  // =========================
  const walletReturnKpis = useMemo(() => {
    const s = Array.isArray(evoSeriesRaw) ? evoSeriesRaw : [];
    if (!s.length) return { dayDeltaPct01: null, periodPct01: null };

    const lastPct = safeNum(todayPoint?.pct01) ?? safeNum(s[s.length - 1]?.pct01);

    let prevPct = null;
    if (todayPoint?.date) {
      const todayISO = String(todayPoint.date).slice(0, 10);
      for (let i = s.length - 1; i >= 0; i--) {
        const d = String(s[i]?.date || "").slice(0, 10);
        if (d && d < todayISO) {
          prevPct = safeNum(s[i]?.pct01);
          break;
        }
      }
    } else if (s.length >= 2) {
      prevPct = safeNum(s[s.length - 2]?.pct01);
    }

    const dayDeltaPct01 =
      lastPct != null && prevPct != null ? lastPct - prevPct : null;

    const firstPct = safeNum(s[0]?.pct01);
    const periodPct01 =
      lastPct != null && firstPct != null ? lastPct - firstPct : null;

    return { dayDeltaPct01, periodPct01 };
  }, [evoSeriesRaw, todayPoint]);

  return (
    <div
      style={styles.page}
      onTouchStart={ptr.handlers.onTouchStart}
      onTouchMove={ptr.handlers.onTouchMove}
      onTouchEnd={ptr.handlers.onTouchEnd}
      onTouchCancel={ptr.handlers.onTouchEnd}
    >
      <PullToRefreshHeader
        height={ptr.height}
        refreshing={ptr.refreshing}
        ready={ptr.ready}
      />

      {!ready ? (
        <div style={styles.bootOverlay}>
          <div style={styles.bootCard}>
            <div style={styles.bootTitle}>Carregando…</div>
            <div style={styles.bootSubtitle}>
              Preparando suas configurações do FinBot
            </div>
          </div>
        </div>
      ) : null}

      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, letterSpacing: -0.3 }}>Carteira</h2>
        </div>
      </header>

      {showFatal ? (
        <section style={styles.card}>
          <div style={{ fontWeight: 950, fontSize: 16, letterSpacing: -0.2 }}>
            Não foi possível atualizar agora
          </div>

          <div style={{ marginTop: 8, opacity: 0.78, lineHeight: 1.35 }}>
            {anyErrObj?.message || "Tente novamente em instantes."}
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              style={styles.btnPrimary}
              type="button"
              onClick={() => summaryQuery.refetch()}
            >
              Tentar novamente
            </button>

            <button
              style={styles.btnGhost}
              type="button"
              onClick={() => navigate(ROUTE_MONITORING)}
            >
              Ir para Monitoring
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.6 }}>
            Dica: verifique sua conexão ou tente novamente em instantes.
          </div>
        </section>
      ) : null}

      {!showFatal ? (
        <>
          {summaryErr ? <div style={styles.error}>⚠ {summaryErr}</div> : null}
          {summaryLoading ? (
            <div style={{ marginTop: 14, opacity: 0.85 }}>Carregando…</div>
          ) : null}

          {!summaryLoading && !summaryErr ? (
            <>
              {/* ✅ Evolução do investimento */}
              <section style={styles.card}>
                <div style={{ ...styles.cardTitle, justifyContent: "space-between" }}>
                  <span>Evolução do investimento</span>

                  <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {PERIODS.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setEvoPeriodKey(p.key)}
                          style={{
                            ...styles.segBtn,
                            ...(evoPeriodKey === p.key ? styles.segBtnActive : null),
                          }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {[
                        { k: "d1", label: "Diário" },
                        { k: "m1", label: "Mensal" },
                      ].map((o) => (
                        <button
                          key={o.k}
                          type="button"
                          onClick={() => setEvoGran(o.k)}
                          style={{
                            ...styles.segBtn,
                            ...(evoGran === o.k ? styles.segBtnActive : null),
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {simErr ? <div style={styles.error}>⚠ método: {simErr}</div> : null}

                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    gridTemplateColumns: isNarrow ? "1fr" : "repeat(3, 1fr)",
                  }}
                >
                  <div style={styles.kpiEquityBox}>
                    <div style={styles.kpiEquityTitle}>Equity total</div>

                    <div style={styles.kpiEquityValue}>
                      {fmtMoney(totalEquityLive, baseCurrency)}
                    </div>

                    <div style={styles.kpiEquitySub}>
                      <span style={styles.kpiEquityLine}>
                        <span style={styles.kpiEquityLabel}>Δ hoje</span>
                        <b
                          style={{
                            color:
                              walletReturnKpis.dayDeltaPct01 > 0
                                ? "rgba(110, 231, 183, 0.95)"
                                : walletReturnKpis.dayDeltaPct01 < 0
                                ? "rgba(252, 165, 165, 0.95)"
                                : "rgba(255,255,255,0.78)",
                          }}
                        >
                          {walletReturnKpis.dayDeltaPct01 != null
                            ? fmtPct01Signed(walletReturnKpis.dayDeltaPct01, 2)
                            : "—"}
                        </b>
                      </span>

                      <span style={styles.kpiEquityLine}>
                        <span style={styles.kpiEquityLabel}>no período ({evoPeriodKey})</span>
                        <b
                          style={{
                            color:
                              walletReturnKpis.periodPct01 > 0
                                ? "rgba(110, 231, 183, 0.95)"
                                : walletReturnKpis.periodPct01 < 0
                                ? "rgba(252, 165, 165, 0.95)"
                                : "rgba(255,255,255,0.78)",
                          }}
                        >
                          {walletReturnKpis.periodPct01 != null
                            ? fmtPct01Signed(walletReturnKpis.periodPct01, 2)
                            : "—"}
                        </b>
                      </span>

                      <span style={styles.kpiEquityLine}>
                        <span style={styles.kpiEquityLabel}>FinBot Alpha (método)</span>
                        <span style={styles.kpiAlphaInline}>
                          {idealReturnSelectedPct01 != null
                            ? fmtPct01Signed(idealReturnSelectedPct01, 2)
                            : "—"}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div style={styles.kpiBox}>
                    <div style={styles.kpiK}>Máx drawdown</div>
                    <div style={styles.kpiV}>
                      <b>{evoKpis.ddPct01 != null ? fmtPct01Signed(evoKpis.ddPct01, 2) : "—"}</b>
                    </div>
                  </div>

                  <div style={styles.kpiBox}>
                    <div style={styles.kpiK}>Operações (período)</div>
                    <div style={styles.kpiV}>
                      <b>
                        buy {evoKpis.buySum != null ? fmtMoney(evoKpis.buySum, baseCurrency) : "—"} • sell{" "}
                        {evoKpis.sellSum != null ? fmtMoney(evoKpis.sellSum, baseCurrency) : "—"}
                      </b>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  {evoErr ? <div style={styles.error}>⚠ {evoErr}</div> : null}
                  {evoLoading ? (
                    <div style={{ marginTop: 10, opacity: 0.85 }}>
                      Carregando evolução…
                    </div>
                  ) : null}

                  {!evoLoading && !evoErr ? (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ height: 92, marginBottom: 8 }}>
                        <PortfolioPLMiniChart
                          data={evoSeries}
                          todayPoint={todayPoint}
                          syncedTimeRange={evoSyncedRange}
                          onTimeRangeChange={setEvoSyncedRange}
                          openedDateISO={openedDateISO}
                        />
                      </div>

                      <div style={{ height: 220 }}>
                        <PortfolioPositionsChart
                          data={evoSeries}
                          todayPoint={todayPoint}
                          currency={baseCurrency}
                          openedDateISO={openedDateISO}
                          syncedTimeRange={evoSyncedRange}
                          onTimeRangeChange={setEvoSyncedRange}
                        />
                      </div>

                      <div style={{ marginTop: 0, fontSize: 12, opacity: 0.62, lineHeight: 1.35 }}>
                        <b>Abaixo:</b> valor das posições (mark-to-market). <br />
                        Barras na base = dias com trades (verde = buy, vermelho = sell, roxo = ambos).
                        <br />
                        <span style={{ opacity: 0.75 }}>Dica (mobile): toque no gráfico para fixar o tooltip.</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>

              <section style={styles.card}>
                <div style={styles.cardTitle}>Carteira agora</div>

                <div
                  style={{
                    ...styles.walletTopRow,
                    gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  }}
                >
                  <div style={styles.walletTopLeft}>
                    <div style={styles.walletTopK}>Carteira</div>
                    <div style={styles.walletTopV}>
                      <b>id: {safeSummary?.walletId ?? "—"}</b>
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.walletTopRight,
                      textAlign: isNarrow ? "left" : "right",
                      alignItems: isNarrow ? "flex-start" : "flex-end",
                    }}
                  />
                </div>

                {isNewEmptyWallet ? (
                  <div style={styles.emptyOnboardingBox}>
                    <div style={{ fontWeight: 950, fontSize: 16, letterSpacing: -0.2 }}>
                      Começar: informe o capital que quer acompanhar
                    </div>

                    <div style={{ marginTop: 6, opacity: 0.75, lineHeight: 1.35 }}>
                      Para trabalharmos, com isso o FinBot calcula reserva, disponível
                      para investir e começa a sugerir ações.
                    </div>

                    <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button style={styles.btnPrimary} onClick={openCashModal} type="button">
                        Definir capital inicial
                      </button>
                    </div>

                    <div style={{ marginTop: 12, display: "grid", gap: 6, fontSize: 12, opacity: 0.8 }}>
                      <div>1) Defina seu capital (2 cliques)</div>
                      <div>2) O app te mostra “o que fazer hoje”</div>
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {holdingsWithWeight.length ? (
                    holdingsWithWeight.map((h) => {
                      const meta = assetMetaOf(h.symbol);

                      const hasPct = h.changePct01 != null && Number.isFinite(Number(h.changePct01));
                      const hasCh = h.change != null && Number.isFinite(Number(h.change));
                      const pct = hasPct ? Number(h.changePct01) : null;
                      const ch = hasCh ? Number(h.change) : null;
                      const last = h.lastPrice != null && Number.isFinite(Number(h.lastPrice)) ? Number(h.lastPrice) : null;

                      const dir = pct != null ? (pct > 0 ? "up" : pct < 0 ? "down" : "flat") : "flat";
                      const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "•";
                      const changeKind = dir === "up" ? "buy" : dir === "down" ? "sell" : "none";

                      const titleChange =
                        pct != null || ch != null
                          ? `Variação no momento: ${pct != null ? fmtPct01Signed(pct, 2) : "—"} • ${
                              ch != null ? fmtMoneySigned(ch, baseCurrency) : "—"
                            }`
                          : "Variação indisponível";

                      return (
                        <button
                          key={h.symbol}
                          style={styles.holdingCard}
                          onClick={() => openAsset(h.symbol)}
                          type="button"
                          title="Abrir detalhe do ativo"
                        >
                          <div style={styles.holdingInner}>
                            <div style={styles.holdingRowTop}>
                              <span style={styles.holdingSymbol}>{h.symbol}</span>

                              <span style={styles.holdingChipWrap} title={titleChange}>
                                <PillTiny kind={changeKind} title={titleChange}>
                                  {arrow} {pct != null ? fmtPct01Signed(pct, 2) : "—"}
                                </PillTiny>
                              </span>

                              <span
                                style={styles.holdingPriceTop}
                                title={last != null ? "Cotação no momento" : "Cotação indisponível"}
                              >
                                {last != null ? fmtMarketPrice(last, baseCurrency) : "—"}
                              </span>
                            </div>

                            <div style={styles.holdingRowBottom}>
                              <span style={styles.holdingName} title={meta?.name || ""}>
                                {meta?.name || " "}
                              </span>

                              <span style={styles.holdingMiniLine}>
                                w: <b>{h.weight != null ? fmtPct01(h.weight, 1) : "—"}</b>{" "}
                                <span style={{ opacity: 0.6 }}>•</span>{" "}
                                total: <b>{h.marketValue != null ? fmtMoney(h.marketValue, baseCurrency) : "—"}</b>
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <span style={{ opacity: 0.65 }}>—</span>
                  )}
                </div>

                {summaryQuery.isFetching ? (
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.65 }}>
                    Atualizando preços…
                  </div>
                ) : quotesErr ? (
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                    ⚠ {quotesErr}
                  </div>
                ) : quotesUpdatedAt ? (
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.6 }}>
                    preços ao vivo:{" "}
                    {quotesUpdatedAt.toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                ) : null}
              </section>
            </>
          ) : null}
        </>
      ) : null}

      {/* ✅ MODAL CAPITAL INICIAL */}
      {cashOpen ? (
        <div style={styles.modalOverlay} onMouseDown={closeCashModal}>
          <div style={styles.modalCardSmall} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 900 }}>Definir capital inicial</div>
              <button style={styles.iconBtn} onClick={closeCashModal} title="Fechar">
                <FiX />
              </button>
            </div>

            <div style={{ marginTop: 8, opacity: 0.75, fontSize: 12, lineHeight: 1.35 }}>
              Isso adiciona caixa na carteira (MVP). Você pode ajustar depois.
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
              <label style={styles.modalField}>
                <span style={styles.modalLabel}>Valor (R$)</span>
                <input
                  value={cashValue}
                  onChange={(e) => setCashValue(e.target.value)}
                  placeholder="ex: 10.000,00"
                  style={styles.modalInput}
                  disabled={cashSubmitting}
                  inputMode="decimal"
                />
              </label>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[1000, 5000, 10000, 20000].map((v) => (
                  <button
                    key={v}
                    type="button"
                    style={styles.quickChip}
                    onClick={() => setCashValue(String(v))}
                    disabled={cashSubmitting}
                    title="Preencher valor"
                  >
                    {fmtMoney(v, baseCurrency)}
                  </button>
                ))}
              </div>

              <div style={{ ...styles.modalField }}>
                <span style={styles.modalLabel}>Preview</span>
                <div style={styles.modalValueBox}>
                  <b>
                    {(() => {
                      const n = toNumSmart(cashValue);
                      if (!Number.isFinite(n) || n <= 0) return "—";
                      return fmtMoney(n, baseCurrency);
                    })()}
                  </b>
                </div>
              </div>
            </div>

            {cashErr ? <div style={styles.error}>⚠ {cashErr}</div> : null}

            <div style={styles.modalFooter}>
              <button style={styles.btnGhost} onClick={closeCashModal} disabled={cashSubmitting}>
                Cancelar
              </button>
              <button style={styles.btnPrimary} onClick={submitCashAdjust} disabled={cashSubmitting}>
                {cashSubmitting ? "Salvando…" : "Adicionar ao caixa"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   subcomponents
========================= */

function PillTiny({ kind = "none", children, title }) {
  const s = {
    ...styles.pillTiny,
    ...(kind === "buy" ? styles.pillTinyBuy : null),
    ...(kind === "sell" ? styles.pillTinySell : null),
    ...(kind === "none" ? styles.pillTinyNone : null),
  };
  return (
    <span style={s} title={title}>
      {children}
    </span>
  );
}


/* ============================================================
   ✅ A PARTIR DAQUI: mantenha exatamente o RESTO do seu Today.jsx
   - principalmente: const styles = { ... }
   - e qualquer outro helper/exports abaixo
============================================================ */


/* =========================
   styles
========================= */

const styles = {
  page: { padding: 14, maxWidth: 1100, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },

  bootOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    zIndex: 1200,
  },
  bootCard: {
    width: "min(520px, 100%)",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(20,20,24,0.98)",
    padding: 16,
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  },
  bootTitle: { fontWeight: 950, fontSize: 16, opacity: 0.95 },
  bootSubtitle: { marginTop: 6, fontSize: 12, opacity: 0.7, lineHeight: 1.35 },

  error: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "rgba(255,120,120,0.10)",
    border: "1px solid rgba(255,120,120,0.25)",
    color: "rgba(255,255,255,0.92)",
  },

  card: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
  },
  cardTitle: {
    fontWeight: 900,
    opacity: 0.95,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap", // ✅ opcional
  },

  walletTopRow: { display: "grid", gap: 12, alignItems: "start" },
  walletTopLeft: { minWidth: 0 },
  walletTopRight: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    textAlign: "right",
  },

  walletTopK: { fontSize: 12, opacity: 0.65 },
  walletTopV: { fontSize: 16, opacity: 0.95, marginTop: 2 },

  emptyOnboardingBox: {
    marginTop: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.03)",
    padding: 12,
  },

  // ✅ Segmented (período / granularidade)
  segBtn: {
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.86)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 850,
    lineHeight: 1,
  },
  segBtnActive: {
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "rgba(255,255,255,0.96)",
  },

  // ✅ KPI boxes
  kpiBox: {
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    padding: 12,
    minWidth: 0,
  },
  kpiK: { fontSize: 12, opacity: 0.68 },
  kpiV: {
    fontSize: 14,            // (antes 15)
    opacity: 0.95,
    marginTop: 4,
    letterSpacing: -0.2,
    fontVariantNumeric: "tabular-nums", // ✅ melhora alinhamento visual
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  pillTinyBuy: {
    background: "rgba(120,255,170,0.10)",
    border: "1px solid rgba(120,255,170,0.22)",
  },
  pillTinySell: {
    background: "rgba(255,120,120,0.10)",
    border: "1px solid rgba(255,120,120,0.22)",
  },
  pillTinyNone: {},

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    zIndex: 1000,
  },
  modalCardSmall: {
    width: "min(560px, 100%)",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(20,20,24,0.98)",
    padding: 14,
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },

  iconBtn: {
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.95)",
    borderRadius: 12,
    padding: "8px 10px",
    cursor: "pointer",
  },

  modalField: { display: "grid", gap: 6 },
  modalLabel: { fontSize: 12, opacity: 0.7 },

  modalInput: {
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.95)",
    outline: "none",
    fontSize: 16,
  },

  modalValueBox: {
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.95)",
    fontSize: 16,
  },

  modalFooter: {
    marginTop: 14,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },

  btnGhost: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.96)",
    cursor: "pointer",
    fontWeight: 850,
  },

  quickChip: {
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    borderRadius: 999,
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  holdingCard: {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    borderRadius: 14,
    padding: "10px 12px",
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
  },

  holdingInner: { display: "grid", gap: 4, minWidth: 0 },

  holdingRow1: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "baseline",
    gap: 10,
    minWidth: 0,
  },

  holdingLeftTop: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 8,
    minWidth: 0,
  },

  holdingSymbol: { fontWeight: 950, letterSpacing: 0.2 },

  holdingRightTop: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 10,
    justifyContent: "flex-end",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  holdingKV: { opacity: 0.78 },

  holdingName: {
    fontSize: 12,
    opacity: 0.78,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
    holdingSymbolBlock: {
    display: "grid",
    gap: 2,
    lineHeight: 1.05,
  },
  holdingScore: {
    fontSize: 11,
    opacity: 0.72,
    letterSpacing: 0.2,
  },
  kpiAlphaBox: {
      background:
        "linear-gradient(180deg, rgba(80,160,120,0.16), rgba(0,0,0,0))",
      border: "1px solid rgba(120,200,160,0.35)",
      borderRadius: 16,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },

    kpiAlphaTitle: {
      fontSize: 13,
      opacity: 0.75,
      letterSpacing: 0.4,
    },

    kpiAlphaValue: {
      fontSize: 20,
      // fontWeight: 300,
      // letterSpacing: -0.8,
      lineHeight: 1.05,
    },

    kpiAlphaSub: {
      marginTop: 6,
      fontSize: 12,
      opacity: 0.65,
    },
    holdingPrice: {
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: -0.1,
      opacity: 0.92,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    },
    kpiEquityBox: {
      background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(0,0,0,0))",
      border: "1px solid rgba(255,255,255,0.20)",
      borderRadius: 16,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minWidth: 0,
    },
    kpiEquityTitle: {
      fontSize: 13,
      opacity: 0.72,
      letterSpacing: 0.4,
    },
    kpiEquityValue: {
      marginTop: 4,
      fontSize: 22,
      fontWeight: 950,
      letterSpacing: -0.4,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.05,
    },
    kpiEquitySub: {
      marginTop: 8,
      fontSize: 12,
      opacity: 0.88,
      display: "grid",
      gap: 4,
    },
    kpiEquityLine: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 10,
    },
    kpiEquityLabel: {
      opacity: 0.7,
      whiteSpace: "nowrap",
    },
    holdingInner: { display: "grid", gap: 4, minWidth: 0 },

    holdingRowTop: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      minWidth: 0,
    },

    holdingSymbol: {
      fontWeight: 950,
      letterSpacing: 0.2,
      flexShrink: 0,           // ✅ nunca espreme o símbolo
    },

    holdingChipWrap: {
      minWidth: 0,             // ✅ permite ellipsis dentro do flex
      flex: "1 1 auto",        // ✅ chip ocupa o meio e encolhe
      maxWidth: "46%",         // ✅ limite p/ sobrar espaço pro preço
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
       display: "flex",   
    },

    holdingPriceTop: {
      marginLeft: "auto",      // ✅ empurra o preço p/ direita
      flexShrink: 0,           // ✅ não quebra o preço
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: -0.1,
      opacity: 0.92,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    },

    holdingRowBottom: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 10,
      minWidth: 0,
    },

    holdingName: {
      fontSize: 12,
      opacity: 0.78,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },

    holdingMiniLine: {
      fontSize: 11,
      opacity: 0.78,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
      flexShrink: 0,
    },
    pillTiny: {
      fontSize: 10,
      padding: "2px 7px",
      borderRadius: 999,
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.06)",
      opacity: 0.95,
      whiteSpace: "nowrap",
    },
};
